import { useEffect, useState } from "react";
import { ApiError, fetchStepUpEvidence } from "../api";
import type { StepUpEvidence } from "../types";

/**
 * Before/after: does step-up verification actually reduce human work?
 *
 * Every number here is read from `GET /v1/evidence/step-up`, which projects
 * `ml/artifacts/evaluation_v2.json` and `ml/artifacts/ceiling_analysis.json`. Nothing is
 * computed in the browser. When the artifacts are missing this renders "Not measured yet"
 * rather than a plausible-looking placeholder.
 */

function pct(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * The backend reports variant/criterion names as engineering identifiers (they key into
 * ml/artifacts/evaluation_v2.json). Visitors read a plain-Korean translation; unknown
 * names fall back to the raw identifier rather than hiding data.
 */
const VARIANT_LABELS: Record<string, { icon: string; label: string; desc: string }> = {
  pessimistic: {
    icon: "😰",
    label: "불리한 조건",
    desc: "사기꾼이 본인확인을 훨씬 잘 뚫는다고 가정",
  },
  baseline: { icon: "🙂", label: "기본 가정", desc: "위 표의 결과를 낸 가정" },
  optimistic: {
    icon: "😎",
    label: "유리한 조건",
    desc: "사기꾼이 본인확인을 거의 못 뚫는다고 가정",
  },
};

const CRITERIA_LABELS: Record<string, string> = {
  separation_above_floor: "진짜 주인과 사기꾼의 통과율 차이가 충분히 큰가",
  recall_not_materially_worse_than_v1: "사기 잡는 비율이 이전보다 나빠지지 않았는가",
  intervention_reduction_meets_minimum: "사람이 봐야 하는 일이 약속한 만큼 줄었는가",
  step_up_share_within_limit: "확인 알림을 너무 자주 보내 고객을 귀찮게 하지 않는가",
};

export function InterventionImpact() {
  const [evidence, setEvidence] = useState<StepUpEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStepUpEvidence()
      .then(setEvidence)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 404
            ? "Not measured yet — ml/artifacts/evaluation_v2.json이 없습니다."
            : "Not measured yet — 평가 결과를 불러오지 못했습니다.",
        );
      });
  }, []);

  if (error) {
    return (
      <section className="card">
        <h3>📱 &quot;본인 맞으세요?&quot; 한 번 물어보면 어떻게 될까</h3>
        <p className="muted">{error}</p>
      </section>
    );
  }
  if (!evidence) {
    return (
      <section className="card">
        <h3>📱 &quot;본인 맞으세요?&quot; 한 번 물어보면 어떻게 될까</h3>
        <p className="muted">불러오는 중…</p>
      </section>
    );
  }

  const { before, after, modelling_ceiling: ceiling } = evidence;

  return (
    <section className="card">
      <h3>📱 &quot;본인 맞으세요?&quot; 한 번 물어보면 어떻게 될까</h3>
      <p>
        애매한 거래가 오면 사람에게 넘기기 전에, 먼저 <strong>고객 폰으로 &quot;본인이 보낸 것
        맞나요?&quot; 알림을 자동으로</strong> 보내보면 어떨까요? 진짜 주인이면 바로 누를 거고,
        계좌를 훔친 사기꾼은 그 폰이 없으니 못 누르겠죠. 그 실험의 결과가 아래 표예요.
      </p>
      <p className="muted">
        고정 테스트셋 n={evidence.test_n.toLocaleString()}에서, 임계값을 검증셋에서 고정한 뒤
        1회 측정한 결과입니다.
      </p>

      <p className="callout">
        왜 이 방법이냐면 — AI 모델을 더 좋게 만드는 길은 이미 한계까지 갔거든요. 계산해 보니
        지금 모델이 데이터에서 얻을 수 있는 신호의{" "}
        <strong>{pct(ceiling.ml_share_of_achievable_signal)}</strong>를 이미 다 쓰고 있었어요
        (이론적 최대치 대비). 남은 길은 모델을 쥐어짜는 게 아니라{" "}
        <strong>새로운 정보를 만들어내는 것</strong> — 고객의 응답이 바로 그 새 정보입니다.
      </p>

      <table className="evidence-table impact-table">
        <thead>
          <tr>
            <th></th>
            <th>이전 (모델만)</th>
            <th>이후 (모델 + 자동 확인)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="highlight">
            <td>사람이 봐야 하는 비율</td>
            <td>{pct(before.human_intervention_rate)}</td>
            <td>
              <strong>{pct(after.human_intervention_rate)}</strong>
            </td>
          </tr>
          <tr>
            <td>사기를 잡아낸 비율 (Recall)</td>
            <td>{(before.fraud_recall * 100).toFixed(2)}%</td>
            <td>{(after.fraud_recall * 100).toFixed(2)}%</td>
          </tr>
          <tr>
            <td>놓친 사기 (자동 통과돼 버린 것)</td>
            <td>{before.fraud_auto_approved}건</td>
            <td>{after.fraud_auto_approved}건</td>
          </tr>
          <tr>
            <td>사람 검토 건수</td>
            <td>{before.human_reviews.toLocaleString()}건</td>
            <td>{after.human_reviews.toLocaleString()}건</td>
          </tr>
        </tbody>
      </table>

      <p>
        사람이 봐야 하는 일이 <strong>{pct(evidence.absolute_intervention_reduction)}p
        줄었어요</strong> (상대적으로{" "}
        <strong>{(evidence.relative_intervention_reduction * 100).toFixed(1)}%</strong> 감소).
        보통은 사람 일을 줄이면 놓치는 사기가 늘어나는 시소 관계인데, 여기서는{" "}
        <strong>둘 다 좋아졌어요</strong> — 하나를 내주고 하나를 얻은 게 아닙니다.
      </p>

      <h4>🧪 혹시 운이 좋았던 건 아닐까? — 불리한 조건으로 다시 시험 보기</h4>
      <p>
        위 결과에는 약점이 하나 있어요. &quot;확인 알림을 몇 %가 통과하는가&quot;는 실제로
        재본 게 아니라 <strong>가정한 숫자</strong>거든요. 그래서 우리한테 유리한 문제만
        풀고 합격했다는 소리를 안 들으려고, <strong>일부러 불리한 조건으로 시험을 두 번 더
        봤습니다</strong> — 사기꾼이 확인을 훨씬 잘 뚫는 세상이라면? 거의 못 뚫는
        세상이라면?
      </p>
      <table className="evidence-table">
        <thead>
          <tr>
            <th>어떤 세상?</th>
            <th>진짜 주인이 확인을 누를 확률</th>
            <th>사기꾼이 뚫을 확률</th>
            <th>사람이 봐야 하는 비율</th>
            <th>결과</th>
          </tr>
        </thead>
        <tbody>
          {evidence.sensitivity.map((variant) => {
            const label = VARIANT_LABELS[variant.name];
            return (
              <tr key={variant.name}>
                <td>
                  {label ? (
                    <>
                      {label.icon} <strong>{label.label}</strong>
                      <div className="muted">{label.desc}</div>
                    </>
                  ) : (
                    variant.name
                  )}
                </td>
                <td>{pct(variant.pass_rate_legit)}</td>
                <td>{pct(variant.pass_rate_fraud)}</td>
                <td>{pct(variant.human_intervention_rate)}</td>
                <td>
                  {variant.all_falsification_criteria_passed ? (
                    <span className="badge approve">합격</span>
                  ) : (
                    <span className="badge review">불합격</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className={evidence.falsification_criteria_passed ? "gate-pass" : "gate-fail"}>
        <strong>
          🔒 시험 전에 미리 봉투에 넣어둔 합격 기준 4개 —{" "}
          {evidence.falsification_criteria_passed ? "전부 통과" : "미통과"}
        </strong>
        <p className="muted">
          이 기준들은 <strong>결과를 보기 전에</strong> 파일로 적어 고정했어요. 결과가 나온
          뒤에 &quot;사실 기준을 바꾸면 합격인데요&quot;라고 말을 바꾸는 반칙이 아예 불가능하게요.
        </p>
        <ul>
          {Object.entries(evidence.falsification_criteria_results).map(([name, passed]) => (
            <li key={name}>
              {passed ? "✅" : "❌"} {CRITERIA_LABELS[name] ?? name}
            </li>
          ))}
        </ul>
        <p className="muted">
          {evidence.passed_under_every_assumption.length === evidence.sensitivity.length
            ? "불리한 조건에서도, 유리한 조건에서도, 세 세상 모두에서 합격 — 이 결론은 운 좋은 가정 하나에 기대고 있지 않습니다."
            : `${evidence.passed_under_every_assumption.join(", ")} 가정에서만 기준을 통과했습니다.`}
        </p>
      </div>
    </section>
  );
}
